import { ApplicationInsights } from '@microsoft/applicationinsights-web'
import {
  analytics,
  type AnalyticsEnvironmentConfig,
  createSegmentDestination,
  initializeSegment,
  insertCookieTrackingScript,
} from '@susu/analytics-client'
import { isValidLocale, Locale } from '@susu/locale-utils'
import { getSegmentCommonProperties } from '@susu/segment-common'

import avo, {
  type ApplicationIdValueType,
  AvoEnv,
  type CountryValueType,
  type CurrencyValueType,
  type LanguageValueType,
  type LastPlatformValueType,
  type LoggedInValueType,
  type PageTypeValueType,
  type PlatformValueType,
  bookingToolAllStyleAdvisors,
  bookingToolCancelConfirmed,
  bookingToolConfirmAppointment,
  bookingToolContinueWithAppointmentSelected,
  bookingToolCountrySelected,
  bookingToolDateSelected,
  bookingToolEmployeeSelected,
  bookingToolError,
  bookingToolNoPreferenceSelected,
  bookingToolOpened,
  bookingToolPanelClosed,
  bookingToolStepCompleted,
  bookingToolStepViewed,
  bookingToolStoreSelected,
  bookingToolTimeslotSelected,
  type CustomDestination,
  type ErrorTypeValueType,
  experimentViewed,
  type FlowTypeValueType,
  preferencesSkipped,
  preferencesSubmitted,
} from '../../../Avo/Avo'
import {
  type Environment,
  INSTRUMENTATION_KEY,
  ONE_TRUST_ID,
  SEGMENT_WRITE_KEY,
} from '../../config'
import { type ScreenType, useBookingStore } from '../../store'

export const APPLICATION_ID = 'booking_tool' as ApplicationIdValueType
const APP_VERSION = '2.0.0'

const silentAvoLogger = {
  logDebug: () => true,
  logWarn: () => true,
  logError: () => true,
} as const

const getAnalyticsEnvironmentConfig = (
  env: Environment,
): AnalyticsEnvironmentConfig => ({
  segmentWriteKey: SEGMENT_WRITE_KEY[env],
  environmentType: env === 'prd' ? 'PRD' : undefined,
  appVersion: APP_VERSION,
})

/**
 * True when the page already owns cookie consent.
 *
 * Consent belongs to the host. `insertCookieTrackingScript` skips a second call
 * through `window.cookieTrackingScriptInserted`, but only for hosts that use
 * that same helper — one loading OneTrust from a plain tag or GTM would get our
 * domain script stacked on its own, pointing the banner at a different consent
 * domain than the page the customer is reading. Standalone
 * (`apps/rp-booking-tool`) is the only host where inserting it is ours to do.
 */
const hostManagesConsent = (): boolean =>
  typeof window !== 'undefined' &&
  (Boolean(window.cookieTrackingScriptInserted) ||
    'OneTrust' in window ||
    'Optanon' in window)

/**
 * `getSegmentCommonProperties()` with the locale guard. Standalone hosts
 * (rp-booking-tool, custom-made) have no locale-prefixed path and the bare call
 * throws without a fallback. A valid locale segment wins, otherwise fall back
 * rather than crash. Shared by init and per-event props: a throw in init skips
 * `avo.initAvo` and silences every event.
 */
function getCommonProperties() {
  const pathSegment = window.location.pathname.split('/').find(Boolean) ?? ''
  return getSegmentCommonProperties(
    isValidLocale(pathSegment) ? undefined : Locale['en-us'],
  )
}

export const initializeAnalytics = (env: Environment) => {
  // `finally` rather than a call at the end of the try below: the caller
  // (`BookingToolRoot`) already tolerates this function throwing (a missing
  // key, a content blocker eating the script), but a throw partway through
  // must not leave `avoEvent`'s queue gated shut for the rest of the flow —
  // every later event would queue forever behind a bootstrap that already
  // failed. `dispatchAvoEvent`'s own try/catch already covers a `fn()` call
  // still failing against an unwired Segment after this.
  try {
    initializeAnalyticsUnsafe(env)
  } finally {
    flushPendingAvoEvents()
  }
}

const initializeAnalyticsUnsafe = (env: Environment) => {
  if (!hostManagesConsent()) {
    insertCookieTrackingScript(ONE_TRUST_ID)
  }

  // Loads the Segment library, and deliberately no-ops when a host already did:
  // the storefront initialises Segment on page load, long before the panel opens.
  initializeSegment<AvoEnv, ApplicationIdValueType>({
    env: getAnalyticsEnvironmentConfig(env),
    applicationId: APPLICATION_ID,
    version: APP_VERSION,
    useWithOneTrust: true,
    omitProperties: ['platform', 'last_platform'],
  })

  /**
   * Wire this package's Avo destination here rather than handing `initAvo` to
   * `initializeSegment`.
   *
   * That function returns early when a host has already initialised Segment, and
   * the early return sits *before* the `initAvo` call. Embedded in the storefront
   * the tool always lost that race — it mounts when the panel opens, long after
   * the host's own init — so this package's generated `Avo.ts` never received a
   * destination: its module-level `Segment` stayed undefined and every event was
   * dropped. Sixteen no-oped through `Segment?.logEvent`; `preferences_submitted`
   * and `preferences_skipped` threw on the one unguarded call. Standalone hosts
   * initialise Segment themselves, which is why only Shopify went quiet.
   *
   * `avo.initAvo` ignores a second call (`__AVO_ENV__ !== null`), so a remount is
   * harmless. `createSegmentDestination` tracks through the shared `analytics`
   * instance, so events reach whichever Segment source the page loaded — the
   * host's when embedded. `applicationId` is what separates these events from
   * the host's. `platform`/`lastPlatform` are schema-wide system properties
   * this source does not send (see the placeholders below).
   */
  const segmentCommon = getCommonProperties()

  avo.initAvo(
    {
      env: env === 'prd' ? AvoEnv.Prod : AvoEnv.Dev,
      webDebugger: false,
      avoLogger: silentAvoLogger,
    },
    {
      applicationId: APPLICATION_ID,
      version: APP_VERSION,
      affiliateMarker: segmentCommon.affiliateMarker,
      awc: segmentCommon.awc,
      // Removed for this source (DA-1554): the omitProperties plugin option strips platform/last_platform before Segment;
      // drop these once Avo marks them never sent for this source.
      platform: '' as PlatformValueType,
      lastPlatform: '' as LastPlatformValueType,
    },
    {},
    createSegmentDestination() as unknown as CustomDestination,
  )
}

export const loadAppInsights = () => {
  const { env } = useBookingStore.getState()
  const applicationInsights = new ApplicationInsights({
    config: {
      instrumentationKey: INSTRUMENTATION_KEY[env],
      enableCorsCorrelation: true,
      disableTelemetry: false,
      disableFetchTracking: true,
      disableAjaxTracking: true,
    },
  })

  applicationInsights.loadAppInsights()

  applicationInsights.addTelemetryInitializer((envelope) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const data = (envelope as any).data?.baseData
    const message =
      data?.message || data?.exception?.message || envelope.name || ''
    if (typeof message === 'string' && message.includes('Script error.')) {
      return false
    }
  })

  useBookingStore.setState({ appInsights: applicationInsights })
}

export const avoIdentify = (userId: string): void => {
  analytics.identify(userId)
}

export const gaAnalytics = ({
  type,
  label,
}: {
  type: string
  label: string
}) => {
  const { location } = useBookingStore.getState()
  if (location === 'cn') return
  if (!window?.dataLayer?.push) return

  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({
    event: 'GAevent',
    eventCategory: 'Booking_Interactions',
    eventAction: type,
    eventLabel: label,
  })
}

export const virtualPageViewAnalytics = ({
  virtualPageTitle,
  virtualPageUrl,
}: {
  virtualPageTitle: string
  virtualPageUrl: string
}) => {
  const { location, userId } = useBookingStore.getState()
  if (location === 'cn') return
  if (!window?.dataLayer?.push) return

  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({ ecommerce: null })
  window.dataLayer.push({
    event: 'virtualPageView',
    virtualPageTitle,
    virtualPageUrl: `/booking-tool${virtualPageUrl}`,
    dimension45: userId,
  })
}

/**
 * Maps each screen to its Avo step number — the same map `step_viewed` and
 * `step_completed` both read, so the two can never disagree about a step's
 * number or name. Matches the Avo schema's own step map exactly:
 * 1 store-locator, 2 employees-list, 3 timeslots, 4 user-data,
 * 5 confirmation, 6 pre-appointment-purpose.
 */
export const mapSteps: Partial<Record<ScreenType, number>> = {
  'store-locator': 1,
  'employees-list': 2,
  timeslots: 3,
  'user-data': 4,
  confirmation: 5,
  'pre-appointment-purpose': 6,
}

function getCookie(name: string): string | undefined {
  const value = `; ${document.cookie}`
  const parts = value.split(`; ${name}=`)
  if (parts.length === 2) return parts.pop()?.split(';').shift()
}

export interface AvoEventOptions {
  step?: number
  name?: string
  /** Dropdown pick for `bookingToolCountrySelected` (lowercase alpha-2). `country` stays the site/locale country. */
  selectedCountry?: string
  ehash?: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  preferences?: any
  storeId?: string
  serviceId?: string
  employeeId?: string
  bookingId?: string
  flowType?: FlowTypeValueType
  errorType?: ErrorTypeValueType
  availableSlots?: number
  selectedDate?: string
  experimentId?: string
  variationId?: string
  /**
   * `bookingToolNoPreferenceSelected` only: true when the Select Sales Rep
   * skip pilot (RP-6031, Avo branch da-1574) auto-selected no-preference on
   * the visitor's behalf; false when they tapped the row themselves.
   *
   * Currently a no-op on the wire — `auto_triggered` only exists on that Avo
   * branch, not yet merged to main, so the generated `bookingToolNoPreferenceSelected`
   * doesn't push it into `eventPropertiesArray` regardless of what's passed
   * here. Once the branch merges and `avo pull` regenerates `Avo/Avo.ts`, this
   * starts being sent with no further code change.
   */
  autoTriggered?: boolean
}

/**
 * Known Avo event names used by booking-tool-v2.
 * Typed to catch misspellings at compile time.
 */
export type AvoEventName =
  | 'bookingToolAllStyleAdvisors'
  | 'bookingToolCancelConfirmed'
  | 'bookingToolConfirmAppointment'
  | 'bookingToolContinueWithAppointmentSelected'
  | 'bookingToolCountrySelected'
  | 'bookingToolDateSelected'
  | 'bookingToolEmployeeSelected'
  | 'bookingToolError'
  | 'bookingToolNoPreferenceSelected'
  | 'bookingToolOpened'
  | 'bookingToolPanelClosed'
  | 'bookingToolStepCompleted'
  | 'bookingToolStepViewed'
  | 'bookingToolStoreSelected'
  | 'bookingToolTimeslotSelected'
  | 'experimentViewed'
  | 'preferencesSkipped'
  | 'preferencesSubmitted'

// Named dispatch map — avoids dynamic property access and catches misspellings at compile time.
const avoEventMap: Record<
  AvoEventName,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (props: Record<string, any>) => void
> = {
  bookingToolAllStyleAdvisors:
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    bookingToolAllStyleAdvisors as unknown as (
      props: Record<string, any>,
    ) => void,
  bookingToolCancelConfirmed:
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    bookingToolCancelConfirmed as unknown as (
      props: Record<string, any>,
    ) => void,
  bookingToolConfirmAppointment:
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    bookingToolConfirmAppointment as unknown as (
      props: Record<string, any>,
    ) => void,
  bookingToolContinueWithAppointmentSelected:
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    bookingToolContinueWithAppointmentSelected as unknown as (
      props: Record<string, any>,
    ) => void,
  bookingToolCountrySelected:
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    bookingToolCountrySelected as unknown as (
      props: Record<string, any>,
    ) => void,
  bookingToolDateSelected:
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    bookingToolDateSelected as unknown as (props: Record<string, any>) => void,
  bookingToolEmployeeSelected:
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    bookingToolEmployeeSelected as unknown as (
      props: Record<string, any>,
    ) => void,
  bookingToolError:
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    bookingToolError as unknown as (props: Record<string, any>) => void,
  bookingToolNoPreferenceSelected:
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    bookingToolNoPreferenceSelected as unknown as (
      props: Record<string, any>,
    ) => void,
  bookingToolOpened:
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    bookingToolOpened as unknown as (props: Record<string, any>) => void,
  bookingToolPanelClosed:
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    bookingToolPanelClosed as unknown as (props: Record<string, any>) => void,
  bookingToolStepCompleted:
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    bookingToolStepCompleted as unknown as (props: Record<string, any>) => void,
  bookingToolStepViewed:
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    bookingToolStepViewed as unknown as (props: Record<string, any>) => void,
  bookingToolStoreSelected:
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    bookingToolStoreSelected as unknown as (props: Record<string, any>) => void,
  bookingToolTimeslotSelected:
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    bookingToolTimeslotSelected as unknown as (
      props: Record<string, any>,
    ) => void,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  experimentViewed: experimentViewed as unknown as (
    props: Record<string, any>,
  ) => void,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  preferencesSkipped: preferencesSkipped as unknown as (
    props: Record<string, any>,
  ) => void,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  preferencesSubmitted: preferencesSubmitted as unknown as (
    props: Record<string, any>,
  ) => void,
}

/**
 * The standard property bundle every booking event now requires (see
 * `BookingToolStepViewedProperties` in the generated `Avo.ts`).
 *
 * Built at send time, not once, so it always reflects the current URL/cookies
 * rather than whatever was true when the tool mounted. `country`/`language`/
 * `userId` are deliberately left out — the tool's own state is their source of
 * truth (see call site) — and `currency` has no source in this package yet, so
 * it goes out `null` rather than guessing a country → currency table.
 */
function getStandardBookingProperties() {
  const segmentCommon = getCommonProperties()
  const { pageType } = useBookingStore.getState()

  return {
    eventLabel: null,
    eventLocation: 'booking_tool',
    currency: null as CurrencyValueType | null,
    // No host pageType (standalone tool) -> 'booking_tool', not segment-common's
    // path-based value, which is 'home' at `/`.
    pageType: (pageType ?? 'booking_tool') as PageTypeValueType,
    url: segmentCommon.url,
    locationId: segmentCommon.location_id,
    loggedIn: segmentCommon.logged_in as LoggedInValueType,
    gaClientId: segmentCommon.ga_client_id,
    gaSessionId: segmentCommon.ga_session_id,
    gaSessionNumber: segmentCommon.ga_session_number,
    fbFbp: segmentCommon.fb_fbp,
    fbFbc: segmentCommon.fb_fbc,
    gaAwClid: segmentCommon.ga_aw_clid,
    ttclid: segmentCommon.ttclid ?? '',
    msclkid: segmentCommon.msclkid ?? '',
    twclid: segmentCommon.twclid ?? '',
    // ponytail: rdt_cid/epik aren't tracked in @susu/segment-common's
    // marketing click IDs yet — stubbed empty until that lands, same as the
    // storefront's own getCommonProperties().
    rdtCid: '',
    epik: '',
    awc: segmentCommon.awc,
  }
}

// Gates `avoEvent` shut for one flow while Avo's Segment destination isn't
// wired yet — see `beginAnalyticsBootstrap` / `flushPendingAvoEvents` below.
// Open by default so every other caller (this package's own tests included)
// keeps firing immediately, exactly as before this queue existed.
let analyticsReady = true
let pendingAvoEvents: { event: AvoEventName; options?: AvoEventOptions }[] = []

/**
 * Closes the gate for the flow `BookingToolRoot` is about to bootstrap.
 *
 * Call this before anything else can call `avoEvent` — including the very
 * first `bookingToolOpened` — so every event of the flow queues in the order
 * it was fired, rather than some going out immediately and others (fired
 * before Avo is wired) being silently dropped by the try/catch below.
 */
export function beginAnalyticsBootstrap(): void {
  analyticsReady = false
  pendingAvoEvents = []
}

/**
 * Opens the gate and replays every queued event, in order.
 *
 * Called at the end of `initializeAnalytics`, once `avo.initAvo` has wired the
 * Segment destination. Screens can mount — and fire `bookingToolStepViewed`
 * — before that effect ever runs: on first mount, a child's own effect
 * commits before its parent's, and `initializeAnalytics` lives in
 * `BookingToolRoot`'s effect. Before this queue existed, `dispatchAvoEvent`'s
 * try/catch swallowed the resulting throw (Avo's `Segment` was still
 * undefined) silently, so the flow's first `booking_tool_step_viewed` — and
 * GrowthBook's `experimentViewed` (`growthbook.ts`'s `trackingCallback`,
 * itself routed through `avoEvent`) — never reached Segment. Queuing here,
 * the one place every caller routes through, covers both without a special
 * case for either.
 */
function flushPendingAvoEvents(): void {
  analyticsReady = true
  const queued = pendingAvoEvents
  pendingAvoEvents = []
  queued.forEach(({ event, options }) => dispatchAvoEvent(event, options))
}

export const avoEvent = (event: AvoEventName, options?: AvoEventOptions) => {
  if (!analyticsReady) {
    pendingAvoEvents.push({ event, options })
    return
  }
  dispatchAvoEvent(event, options)
}

function dispatchAvoEvent(
  event: AvoEventName,
  options?: AvoEventOptions,
): void {
  const state = useBookingStore.getState()

  // Skip analytics in China where Google services are blocked
  if (state.location === 'cn') return

  const fn = avoEventMap[event]
  if (typeof fn !== 'function') return

  /**
   * Tracking never takes the booking down.
   *
   * `Avo/Avo.ts` reaches its Segment destination unguarded for
   * `preferences_submitted` and `preferences_skipped` — `Segment.logEvent(...)`,
   * where the other sixteen events generate `Segment?.logEvent`. So when
   * Segment never initialised (no OneTrust consent, a blocked script, an init
   * that threw — all of which `BookingToolRoot` deliberately tolerates), the
   * pre-appointment Finish and Skip handlers threw "Cannot read properties of
   * undefined (reading 'logEvent')" before they could navigate, and the customer
   * was stuck on a screen with a booking already created.
   *
   * The generated file must not be hand-patched (`avo pull` overwrites it), so
   * the guard sits here and covers every event and every future codegen gap.
   */
  try {
    fn({
      ...getStandardBookingProperties(),
      employeeId: options?.employeeId ?? state.employeeId ?? '',
      language: state.language as LanguageValueType,
      country: state.location as CountryValueType,
      userId: state.userId ?? getCookie('uid'),
      step: options?.step ?? mapSteps[state.screen],
      name: options?.name ?? state.screen,
      serviceId: options?.serviceId ?? state.serviceId ?? '',
      storeId: options?.storeId ?? state.storeId ?? '',
      // The store's location code (e.g. SR-US-Atlanta-Lenox), which joins
      // dim__location.location_code. Set by the host or by StoreLocator.
      locationCode: state.locationCode ?? '',
      bookingId: options?.bookingId ?? state.bookingId ?? '',
      eHash: options?.ehash ?? '',
      // UUID created once per mount (BookingToolRoot) — groups every event of
      // one booking flow. Added here rather than at each call site so no
      // future event can forget it.
      flowId: state.flowId ?? '',
      ...(options?.flowType !== undefined
        ? { flowType: options.flowType }
        : {}),
      ...(options?.errorType !== undefined
        ? { errorType: options.errorType }
        : {}),
      ...(options?.availableSlots !== undefined
        ? { availableSlots: options.availableSlots }
        : {}),
      ...(options?.selectedCountry !== undefined
        ? { selectedCountry: options.selectedCountry }
        : {}),
      ...(options?.selectedDate !== undefined
        ? { selectedDate: options.selectedDate }
        : {}),
      ...(options?.experimentId !== undefined
        ? { experimentId: options.experimentId }
        : {}),
      ...(options?.variationId !== undefined
        ? { variationId: options.variationId }
        : {}),
      ...(options?.preferences ? { preferences: options.preferences } : {}),
      ...(options?.autoTriggered !== undefined
        ? { autoTriggered: options.autoTriggered }
        : {}),
    })
  } catch (error) {
    console.warn(`Avo event "${event}" skipped:`, error)
  }
}
